import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router';
import {
  Alert,
  Badge,
  Box,
  Button,
  ColumnLayout,
  Container,
  Header,
  KeyValuePairs,
  Link as CloudscapeLink,
  Modal,
  SpaceBetween,
  Spinner,
  StatusIndicator,
  Table,
  Tabs,
} from '@cloudscape-design/components';
import type { RuleDetails, RuleInput, RuleSummary } from '@amzn/helianthus-arbiter-client';
import { ResourceNotFoundException } from '@amzn/helianthus-arbiter-client';
import { useArbiterClient } from '../../context/ArbiterClientContext';
import { RuleStatusBadge } from '../../components/RuleStatusBadge';
import { ArnDisplay } from '../../components/ArnDisplay';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { ExpressionEditor } from '../../components/ExpressionEditor';
import { DependencyGraph } from '../../components/DependencyGraph';
import { ErrorState } from '../../components/ErrorState';
import { LoadingState } from '../../components/LoadingState';
import { humanizeFieldName } from '../../utils/formatFieldName';

function formatDate(date: Date | string | undefined): string {
  if (!date) return '--';
  if (date instanceof Date) return date.toLocaleString();
  return new Date(date).toLocaleString();
}

function formatMetadataValue(key: string, value: string): string {
  if (key === 'creation_time' && /^\d{9,13}$/.test(value)) {
    const ts = value.length <= 10 ? Number(value) * 1000 : Number(value);
    return new Date(ts).toLocaleString();
  }
  return value;
}

function getSourceType(input: RuleInput): string {
  if (!input.source) return 'Unknown';
  if (input.source.discovery) return 'Discovery';
  if (input.source.arbiter) return 'Arbiter';
  if (input.source.call) return 'Call';
  if (input.source.rule) return 'Rule';
  return 'Unknown';
}

function getSourceDetail(input: RuleInput): string {
  if (!input.source) return '';
  if (input.source.discovery) return `type: ${input.source.discovery.discoveryType}${input.source.discovery.filter ? `, filter: ${input.source.discovery.filter.expression}` : ''}`;
  if (input.source.arbiter) return `key: ${input.source.arbiter.key}`;
  if (input.source.call) return `key: ${input.source.call.key}`;
  if (input.source.rule) return `target: ${input.source.rule.target}`;
  return '';
}

export function RuleDetailPage() {
  const { ruleName } = useParams<{ ruleName: string }>();
  const navigate = useNavigate();
  const client = useArbiterClient();
  const [rule, setRule] = useState<RuleDetails | null>(null);
  const [parents, setParents] = useState<RuleSummary[]>([]);
  const [children, setChildren] = useState<RuleSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [deleteVisible, setDeleteVisible] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [versions, setVersions] = useState<RuleSummary[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionsError, setVersionsError] = useState<string | null>(null);
  useDocumentTitle(rule ? `Rule: ${rule.ruleName}` : 'Rule');
  const [versionsFetched, setVersionsFetched] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTabId = searchParams.get('tab') || 'overview';

  const fetchVersions = useCallback(() => {
    if (versionsFetched || !ruleName) return;
    setVersionsLoading(true);
    setVersionsError(null);
    client.listRuleVersions({ ruleName })
      .then((res) => { setVersions(res.rules ?? []); setVersionsFetched(true); })
      .catch((err) => setVersionsError(err.message))
      .finally(() => setVersionsLoading(false));
  }, [ruleName, versionsFetched, client]);

  const requestedVersion = searchParams.get('version') || undefined;

  useEffect(() => {
    if (!ruleName) return;
    setLoading(true);
    setError(null);
    client.describeRule({ ruleName, version: requestedVersion, includeDependencies: true })
      .then((res) => {
        if (res.rule) setRule(res.rule);
        setParents(res.parents ?? []);
        setChildren(res.children ?? []);
      })
      .catch((err) => setError(err instanceof Error ? err : new Error(String(err))))
      .finally(() => setLoading(false));
  }, [ruleName, requestedVersion, client]);

  const handleDelete = async () => {
    if (!ruleName) return;
    setDeleting(true);
    try {
      await client.deleteRule({ ruleName });
      navigate('/rules');
    } catch (err: unknown) {
      setError(err instanceof Error ? err : new Error('Delete failed'));
      setDeleteVisible(false);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return <LoadingState label="Loading rule..." />;
  }

  if (error || !rule) {
    const is404 = error instanceof ResourceNotFoundException;
    return (
      <ErrorState
        variant={is404 ? 'not-found' : 'api-error'}
        title={is404 ? 'Rule not found' : 'Error loading rule'}
        message={is404
          ? `The rule "${ruleName}" could not be found. It may have been deleted.`
          : (error?.message || 'Rule not found')
        }
        details={undefined}
        onRetry={!is404 ? () => window.location.reload() : undefined}
        backLabel="Back to Rules"
        backHref="/rules"
      />
    );
  }

  return (
    <SpaceBetween size="l">
      <Header
        variant="h1"
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={() => navigate(`/rules/${ruleName}/edit`)}>Edit</Button>
            <Button variant="normal" onClick={() => setDeleteVisible(true)}>Delete</Button>
          </SpaceBetween>
        }
      >
        {rule.ruleName}
      </Header>

      {requestedVersion && requestedVersion !== '@CURRENT' && rule.canonicalVersion && requestedVersion !== rule.canonicalVersion && (() => {
        const latestParams = new URLSearchParams(searchParams);
        latestParams.delete('version');
        const latestQuery = latestParams.toString();
        const latestHref = `/rules/${encodeURIComponent(ruleName!)}${latestQuery ? `?${latestQuery}` : ''}`;
        return (
          <Alert type="info">
            Viewing version {requestedVersion}. Latest is version {rule.canonicalVersion}.{' '}
            <CloudscapeLink
              href={latestHref}
              onFollow={(e) => {
                e.preventDefault();
                setSearchParams((prev) => {
                  const next = new URLSearchParams(prev);
                  next.delete('version');
                  return next;
                });
              }}
            >
              View latest
            </CloudscapeLink>
          </Alert>
        );
      })()}

      <Tabs
        activeTabId={activeTabId}
        onChange={({ detail }) => {
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            if (detail.activeTabId === 'overview') {
              next.delete('tab');
            } else {
              next.set('tab', detail.activeTabId);
            }
            return next;
          }, { replace: true });
          if (detail.activeTabId === 'versions') fetchVersions();
        }}
        tabs={[
          {
            id: 'overview',
            label: 'Overview',
            content: (
              <Container>
                <ColumnLayout columns={2} variant="text-grid">
                  <KeyValuePairs
                    items={[
                      { label: 'Rule Name', value: rule.ruleName },
                      { label: 'Version', value: rule.version },
                      { label: 'Canonical Version', value: rule.canonicalVersion },
                      { label: 'Status', value: <RuleStatusBadge status={rule.ruleStatus!} /> },
                      { label: 'Severity', value: rule.severity || '-' },
                      { label: 'Description', value: rule.description || '-' },
                    ]}
                  />
                  <KeyValuePairs
                    items={[
                      { label: 'Applies To', value: rule.appliesTo?.join(', ') ?? '--' },
                      { label: 'Created By', value: <ArnDisplay value={rule.createdBy} /> },
                      { label: 'Created At', value: formatDate(rule.createdAt) },
                      { label: 'Last Modified By', value: <ArnDisplay value={rule.lastModifiedBy} /> },
                      { label: 'Last Modified At', value: formatDate(rule.lastModifiedAt) },
                      ...(rule.securityConcern && rule.securityConcern !== 'Unspecified'
                        ? [{ label: 'Security Concern', value: rule.securityConcern }]
                        : []),
                      ...(rule.stateReason ? [{ label: 'State Reason', value: rule.stateReason }] : []),
                    ]}
                  />
                </ColumnLayout>
                {rule.tags && Object.keys(rule.tags).length > 0 && (
                  <Box margin={{ top: 'l' }}>
                    <Header variant="h3">Tags</Header>
                    <KeyValuePairs
                      items={Object.entries(rule.tags).map(([key, value]) => ({
                        label: key,
                        value,
                      }))}
                    />
                  </Box>
                )}
                {rule.metadata && Object.keys(rule.metadata).length > 0 && (
                  <Box margin={{ top: 'l' }}>
                    <Header variant="h3">Metadata</Header>
                    <KeyValuePairs
                      items={Object.entries(rule.metadata).map(([key, value]) => ({
                        label: key,
                        value: formatMetadataValue(key, value),
                      }))}
                    />
                  </Box>
                )}
                {rule.serviceProperties &&
                  Object.values(rule.serviceProperties).some(
                    (values) => Array.isArray(values) && values.length > 0,
                  ) && (
                  <Box margin={{ top: 'l' }}>
                    <Header variant="h3">Service Properties</Header>
                    <KeyValuePairs
                      items={Object.entries(rule.serviceProperties)
                        .filter(([, values]) => Array.isArray(values) && values.length > 0)
                        .map(([key, values]) => ({
                          label: humanizeFieldName(key),
                          value: (
                            <SpaceBetween direction="horizontal" size="xs">
                              {(values as string[]).map((value) => (
                                <Badge key={value} color="blue">{value}</Badge>
                              ))}
                            </SpaceBetween>
                          ),
                        }))}
                    />
                  </Box>
                )}
              </Container>
            ),
          },
          {
            id: 'inputs',
            label: `Inputs (${rule.inputs?.length ?? 0})`,
            content: (
              <Table
                columnDefinitions={[
                  { id: 'name', header: 'Name', cell: (item: RuleInput) => item.name },
                  { id: 'type', header: 'Type', cell: (item: RuleInput) => item.type },
                  { id: 'sourceType', header: 'Source type', cell: (item: RuleInput) => getSourceType(item) },
                  { id: 'sourceDetail', header: 'Source detail', cell: (item: RuleInput) => getSourceDetail(item) },
                ]}
                items={rule.inputs ?? []}
                variant="embedded"
                empty={<Box textAlign="center">No inputs defined</Box>}
              />
            ),
          },
          {
            id: 'evaluation',
            label: 'Evaluation',
            content: (
              <Container>
                <ExpressionEditor
                  expression={rule.evaluation?.expression ?? ''}
                  language={rule.evaluation?.language ?? 'sangheli-1.0'}
                />
              </Container>
            ),
          },
          {
            id: 'graph',
            label: 'Graph',
            content: (
              <Container>
                <DependencyGraph
                  ruleName={rule.ruleName!}
                  severity={rule.severity}
                  parents={parents}
                  children={children}
                />
              </Container>
            ),
          },
          {
            id: 'versions',
            label: 'Versions',
            content: versionsLoading ? (
              <Box textAlign="center" padding={{ top: 'l' }}><Spinner /></Box>
            ) : versionsError ? (
              <StatusIndicator type="error">{versionsError}</StatusIndicator>
            ) : (
              <Table
                columnDefinitions={[
                  {
                    id: 'version',
                    header: 'Version',
                    cell: (item: RuleSummary) => (
                      <Link to={`/rules/${encodeURIComponent(item.ruleName!)}?version=${item.version}`}>
                        {item.version}
                      </Link>
                    ),
                  },
                  { id: 'status', header: 'Status', cell: (item: RuleSummary) => <RuleStatusBadge status={item.ruleStatus!} /> },
                  { id: 'description', header: 'Description', cell: (item: RuleSummary) => item.description || '-' },
                  { id: 'lastModifiedAt', header: 'Modified at', cell: (item: RuleSummary) => formatDate(item.lastModifiedAt) },
                ]}
                items={versions}
                variant="embedded"
                sortingDisabled
                empty={
                  <Box textAlign="center" padding={{ top: 'l' }}>
                    {versionsFetched ? 'No versions found' : 'Select this tab to load versions'}
                  </Box>
                }
              />
            ),
          },
        ]}
      />

      <Modal
        visible={deleteVisible}
        onDismiss={() => setDeleteVisible(false)}
        header="Delete rule"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setDeleteVisible(false)}>Cancel</Button>
              <Button variant="primary" onClick={handleDelete} loading={deleting}>Delete</Button>
            </SpaceBetween>
          </Box>
        }
      >
        Are you sure you want to delete rule <strong>{rule.ruleName}</strong>? This action cannot be undone.
      </Modal>
    </SpaceBetween>
  );
}
